/**
 * Farm & Acre Co - Fee Auto-Add (Unified)
 *
 * Integrates with Dawn's cart drawer by subscribing to cartUpdate events.
 * Adds fees AFTER Dawn adds the main product, then refreshes the drawer.
 *
 * Handles:
 * - SA Biosecurity Levy ($1.50/tag for SA cattle)
 * - Print Setup Fees (Non-Standard $71.50, Complex $137.50)
 * - Print Line Fees ($0.46 to $1.21 per tag)
 */

(async function() {
  'use strict';

  // SPACEY: Import debug system from shared helpers with console.log fallback (prevents silent failures)
  const debug = window.CartSharedHelpers?.createDebugLogger('[FEE DEBUG]') || {
    log: function(message, data) {
      if (data !== null) {
        console.log('[FEE DEBUG]', message, data);
      } else {
        console.log('[FEE DEBUG]', message);
      }
    },
    error: function(message, error) {
      if (error !== null) {
        console.error('[FEE DEBUG]', message, error);
      } else {
        console.error('[FEE DEBUG]', message);
      }
    },
    warn: function(message, data) {
      if (data !== null) {
        console.warn('[FEE DEBUG]', message, data);
      } else {
        console.warn('[FEE DEBUG]', message);
      }
    }
  };

  const debugLog = debug.log;
  const debugError = debug.error;
  const debugWarn = debug.warn;

  debugLog('=== FEE SYSTEM INITIALIZING ===');
  debugLog('Shared helpers available:', typeof window.CartSharedHelpers !== 'undefined');

  // SPACEY: Wait for CartSharedHelpers to be available (prevents timing issues with defer)
  // Script loading order with defer isn't always guaranteed, so we poll briefly
  if (typeof window.CartSharedHelpers === 'undefined') {
    debugLog('Waiting for CartSharedHelpers to load...');
    await new Promise(function(resolve) {
      var checkInterval = setInterval(function() {
        if (typeof window.CartSharedHelpers !== 'undefined') {
          clearInterval(checkInterval);
          debugLog('CartSharedHelpers is now available');
          resolve();
        }
      }, 10);
      // Fallback timeout after 1 second
      setTimeout(function() {
        clearInterval(checkInterval);
        if (typeof window.CartSharedHelpers !== 'undefined') {
          resolve();
        } else {
          debugError('CartSharedHelpers failed to load - fee system will not work');
          resolve(); // Resolve anyway to allow script to continue (will fail gracefully)
        }
      }, 1000);
    });
  }

  // SPACEY: Import fee variant IDs from shared helpers (now guaranteed to be available)
  const SA_LEVY_VARIANT_ID = window.CartSharedHelpers.SA_LEVY_VARIANT_ID;
  const SETUP_FEES = window.CartSharedHelpers.SETUP_FEES;
  const LINE_FEES = window.CartSharedHelpers.LINE_FEES;
  const SIDE_FEES = window.CartSharedHelpers.SIDE_FEES;
  const ALL_SETUP_FEE_IDS = window.CartSharedHelpers.ALL_SETUP_FEE_IDS;
  const ALL_LINE_FEE_IDS = window.CartSharedHelpers.ALL_LINE_FEE_IDS;
  const ALL_SIDE_FEE_IDS = window.CartSharedHelpers.ALL_SIDE_FEE_IDS;
  const ALL_FEE_VARIANT_IDS = window.CartSharedHelpers.ALL_FEE_VARIANT_IDS;
  // FOFIA print fees (laser/pitch) — strictly gated by vendor so Allflex paths are untouched
  const FOFIA_LINE_FEES = window.CartSharedHelpers.FOFIA_LINE_FEES || {};
  const FOFIA_SIDE_FEES = window.CartSharedHelpers.FOFIA_SIDE_FEES || {};
  const FOFIA_FEEDLOT_LASER_FEE = window.CartSharedHelpers.FOFIA_FEEDLOT_LASER_FEE;
  const FOFIA_BACK_PRINT_FEES = window.CartSharedHelpers.FOFIA_BACK_PRINT_FEES || {};

  // SPACEY: Queue-based fee data system to prevent race conditions from rapid submissions
  const feeQueue = [];
  let isProcessingQueue = false;

  /**
   * Generate unique order group ID
   */
  function generateOrderGroupId() {
    return 'og_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }

  /**
   * HARDENING ROUND 2 (2026-08-04, order #4962 — paid add-on lost AGAIN despite
   * the #4512 keepalive/retry fix): the retry only lived as long as the page.
   * The customer configured tags, the companion POST failed on a flaky mobile
   * connection, they came back 57 minutes later on a fresh page load and checked
   * out — nothing remembered that the buttons/fees were still owed.
   *
   * Fix: every captured add-on group is persisted to localStorage BEFORE any
   * delivery attempt, with its full /cart/add.js payload (variant ids resolved
   * at capture time, on the PDP, where the Liquid data attributes live). The
   * entry is only removed after the items are VERIFIED present in /cart.js.
   * addon-recovery.js (loaded site-wide from theme.liquid) replays unfinished
   * entries on every page load and on checkout intent — so a delivery failure
   * now has to survive every subsequent page view to become a broken order,
   * instead of winning the moment one POST failed twice.
   */
  const ADDON_QUEUE_KEY = 'fac-addon-queue-v1';

  function loadPersistedQueue() {
    try {
      const raw = localStorage.getItem(ADDON_QUEUE_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) { return []; }
  }

  function savePersistedQueue(entries) {
    try {
      if (entries.length) localStorage.setItem(ADDON_QUEUE_KEY, JSON.stringify(entries));
      else localStorage.removeItem(ADDON_QUEUE_KEY);
    } catch (e) { /* private mode etc. — degrade to in-memory behaviour */ }
  }

  function persistAddonEntry(entry) {
    const q = loadPersistedQueue().filter((e) => e.og !== entry.og);
    q.push(entry);
    savePersistedQueue(q);
  }

  function stampAddonAttempt(orderGroupId) {
    const q = loadPersistedQueue();
    const entry = q.find((e) => e.og === orderGroupId);
    if (entry) { entry.lastAttempt = Date.now(); savePersistedQueue(q); }
  }

  function unpersistAddonEntry(orderGroupId) {
    savePersistedQueue(loadPersistedQueue().filter((e) => e.og !== orderGroupId));
  }

  /**
   * A fetch that cannot hang forever. Without this, one stalled POST held the
   * isProcessingQueue mutex for the life of the page, so every later order
   * group (and the pagehide flush) was silently dropped.
   */
  function fetchWithTimeout(url, opts, ms) {
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), ms || 12000);
    return fetch(url, Object.assign({}, opts, { signal: ctrl.signal }))
      .finally(() => clearTimeout(timer));
  }

  /**
   * SPACEY: Get selected color for button pairing (same logic as button-pairing.js)
   */
  function getSelectedColorForButton() {
    // Check radio buttons first (visual_tag swatch layout)
    const colorRadio = document.querySelector('[data-allflex-field="color"]:checked');
    if (colorRadio) return colorRadio.value;

    // Check select dropdown (nlis forms)
    const colorSelect = document.querySelector('[data-allflex-field="color"]');
    if (colorSelect && colorSelect.tagName === 'SELECT' && colorSelect.value) return colorSelect.value;

    return null;
  }

  /**
   * FIX (2026-07-09, order #4278 — uncharged $46 print fee): re-sync the hidden
   * print-line fee mirrors from the live Print Style selects.
   *
   * The mirrors are normally synced by print-line-inputs.js on the select's
   * `change` event only. Browser form restoration (back-nav / autofill) can
   * re-select a paid program WITHOUT firing `change`, leaving the mirror on its
   * free default ("blank") while the visible select shows e.g. IG1000000. The
   * fee detection below reads the mirror, so the print fee silently fails OPEN
   * (print produced, never charged) and the order records "Print Lines: blank"
   * while Print Details says "1 Line". The select's selected-option
   * data-fee-value is the same source of truth tag-order-summary.js already
   * trusts — mirror it here before anything reads the hidden inputs.
   *
   * Deliberately does NOT re-dispatch `change`: that path re-runs
   * updateVisibleLinesForProgram, which clears the customer's typed line text.
   */
  function syncPrintFeeMirrors() {
    const pairs = [
      ['[data-allflex-field="print-style"]', 'allflex-print-lines-hidden'],
      ['[data-allflex-field="print-style-female"]', 'female-print-lines-hidden'],
      ['[data-allflex-field="print-style-male"]', 'male-print-lines-hidden']
    ];
    for (const [styleSel, mirrorId] of pairs) {
      const select = document.querySelector(styleSel);
      const mirror = document.getElementById(mirrorId);
      if (!select || !mirror || select.tagName !== 'SELECT') continue;
      const selectedOption = select.options[select.selectedIndex];
      const feeValue = (selectedOption && selectedOption.dataset.feeValue) || 'blank';
      if (mirror.value !== feeValue) {
        debugWarn('Print-fee mirror desync repaired:', { mirror: mirrorId, was: mirror.value, now: feeValue });
        mirror.value = feeValue;
      }
    }
    // Sides: the fee below reads the live <select> directly, but the order
    // property (properties[Print Sides]) comes from the hidden mirror — keep the
    // order record honest too.
    const sidesSelect = document.querySelector('select[data-allflex-field="print-sides"]');
    const sidesMirror = document.getElementById('print-sides-hidden');
    if (sidesSelect && sidesMirror && sidesMirror.value !== sidesSelect.value) {
      debugWarn('Print-sides mirror desync repaired:', { was: sidesMirror.value, now: sidesSelect.value });
      sidesMirror.value = sidesSelect.value;
    }
  }

  /**
   * Capture form data before Dawn processes the submit
   * This runs in capture phase, before Dawn's handler
   */
  function captureFormData(e) {
    // SPACEY: Debug double-capture issue - track timing and source
    const now = Date.now();
    const timeDelta = window._lastCaptureTime ? now - window._lastCaptureTime : 0;
    debugLog('=== FORM SUBMITTED - CAPTURE PHASE ===', `Timestamp: ${now}, Delta: ${timeDelta}ms`);
    debugLog('Event target:', e.target.tagName, e.target.id);
    debugLog('Submit type:', e.submitter ? e.submitter.type : 'unknown', e.submitter?.id || 'no id');

    // SPACEY: Track timing between captures to identify double-submit pattern
    window._lastCaptureTime = now;
    window._captureCount = (window._captureCount || 0) + 1;

    const form = e.target;
    debugLog('Form element:', form);
    debugLog('Form action:', form.getAttribute('action'));

    // SPACEY: Detect NEW submission by checking if form data has changed
    // This ensures different configurations get different Order Group IDs
    // while multiple captures of SAME submission share the same Order Group ID
    const currentFormData = JSON.stringify(Array.from(new FormData(form).entries()));
    if (window._lastFormData !== currentFormData) {
      // SPACEY: NEW submission detected - clear Order Group input for fresh ID
      const orderGroupInput = form.querySelector('input[name="properties[_Order Group]"]');
      if (orderGroupInput) {
        debugLog('NEW submission detected - clearing Order Group input');
        orderGroupInput.value = '';
      }
      window._lastFormData = currentFormData;
    } else {
      debugLog('SAME submission - reusing Order Group input');
    }
    
    // Tolerant match (not exact-attribute): under Shopify Markets/locale roots
    // the action renders as e.g. /en-au/cart/add and an exact match would skip
    // capture entirely while properties[Matching Buttons]=Yes still submitted.
    const formAction = form.getAttribute('action') || '';
    if (formAction.indexOf('/cart/add') === -1) {
      debugLog('Form does not match cart/add action, skipping');
      return;
    }

    // Repair any mirror desync BEFORE fee detection reads the hidden inputs
    // (also runs on pageshow — see init). This is the order-#4278 fix.
    syncPrintFeeMirrors();

    debugLog('Checking for fee-eligible selectors...');

    // Check if this is a product that needs fees
    const printTypeRadio = document.querySelector('[data-allflex-field="print-type"]:checked');
    const printLinesSelect = document.querySelector('[data-allflex-field="print-lines"]');
    const printLinesFemale = document.querySelector('[data-allflex-field="print-lines-female"]');
    const printLinesMale = document.querySelector('[data-allflex-field="print-lines-male"]');
    const printSidesSelect = document.querySelector('[data-allflex-field="print-sides"]');
    const stateSelect = document.getElementById('allflex-state');
    const saLevyCheckbox = document.getElementById('sa-levy-checkbox');

    // FOFIA fee detection — vendor + fee mode are exposed on <product-info>; Print Method radio
    // (Laser/Pitch Black) only exists on FOFIA line-print tags.
    const fofiaVendorEl = document.querySelector('[data-product-vendor]');
    const isFofia = (fofiaVendorEl && fofiaVendorEl.dataset.productVendor) === 'FOFIA';
    const fofiaFeeMode = (fofiaVendorEl && fofiaVendorEl.dataset.fofiaFeeMode) || ''; // 'lines' | 'feedlot' | 'vid'
    const fofiaPrintMethod = document.querySelector('[data-allflex-field="print-method"]:checked');
    const fofiaBackPrint = document.querySelector('[data-allflex-field="fofia-back-print"]');

    debugLog('Selectors found:', {
      printTypeRadio: !!printTypeRadio,
      printLinesSelect: !!printLinesSelect,
      printLinesFemale: !!printLinesFemale,
      printLinesMale: !!printLinesMale,
      printSidesSelect: !!printSidesSelect,
      stateSelect: !!stateSelect,
      saLevyCheckbox: !!saLevyCheckbox
    });

    // Determine if any fees apply
    debugLog('Checking fee eligibility...');
    debugLog('Print type radio value:', printTypeRadio?.value);
    debugLog('Setup fee for print type:', printTypeRadio ? SETUP_FEES[printTypeRadio.value] : 'N/A');
    debugLog('Print lines value:', printLinesSelect?.value);
    debugLog('Print lines female value:', printLinesFemale?.value);
    debugLog('Print lines male value:', printLinesMale?.value);
    debugLog('Print sides value:', printSidesSelect?.value);
    debugLog('State value:', stateSelect?.value);
    debugLog('SA Levy checkbox checked:', saLevyCheckbox?.checked);

    // SPACEY: Check for button pairing BEFORE fee detection (must run even when no fees)
    // Mandatory Yes/No radio: only the "Yes" choice adds the matching button product.
    const buttonYesRadio = document.getElementById('matching-buttons-yes');
    const buttonPairingEl = document.getElementById('AllflexButtonPairing');
    const hasButtonPairing = buttonYesRadio && buttonYesRadio.checked && buttonPairingEl;

    const hasPrintType = printTypeRadio && SETUP_FEES[printTypeRadio.value];
    const hasPrintLines = (printLinesSelect && printLinesSelect.value && printLinesSelect.value !== 'blank') ||
                          (printLinesFemale && printLinesFemale.value && printLinesFemale.value !== 'blank') ||
                          (printLinesMale && printLinesMale.value && printLinesMale.value !== 'blank');
    const hasPrintSides = printSidesSelect && printSidesSelect.value && printSidesSelect.value !== 'No Print';
    const hasSaLevy = stateSelect?.value === 'SA' && saLevyCheckbox?.checked;

    debugLog('Fee and button detection results:', {
      hasPrintType,
      hasPrintLines,
      hasPrintSides,
      hasSaLevy,
      hasButtonPairing
    });

    // SPACEY: Allow capture to proceed if fees OR button pairing are selected
    if (!hasPrintType && !hasPrintLines && !hasPrintSides && !hasSaLevy && !hasButtonPairing) {
      debugLog('No fees or button pairing, skipping capture');
      return;
    }

    debugLog('=== FEES DETECTED - CAPTURING DATA ===');

    // SPACEY: Get quantity from the product form only (not from cart drawer)
    // Dawn renders the visible quantity input OUTSIDE the <form> element, associated
    // via the form="" attribute — form.querySelector (descendant-only) NEVER finds it,
    // so quantity silently fell back to 1 (2026-07-16 live undercharge: Print Line Fee
    // x1 on a 100-tag order when quantity was set after print options). form.elements
    // DOES include form-attribute-associated controls, so read it first; keep the old
    // selectors as fallbacks for any template that inlines the input.
    let qtyEl = form.elements['quantity'];
    if (qtyEl && typeof qtyEl.item === 'function') qtyEl = qtyEl.item(0); // RadioNodeList guard
    const quantityInput = qtyEl ||
                          form.querySelector('[name="quantity"]') ||
                          form.querySelector('quantity-input .quantity__input');
    const quantity = parseInt(quantityInput?.value) || 1;

    debugLog('Quantity input element:', quantityInput);
    debugLog('Quantity value:', quantity);

    // SPACEY: Always generate a fresh Order Group ID for each form submission
    // This prevents multiple main products from sharing the same Order Group ID
    // which causes fee protection sync issues (multiple parents for one fee)
    let orderGroupInput = form.querySelector('input[name="properties[_Order Group]"]');
    if (!orderGroupInput) {
      orderGroupInput = document.createElement('input');
      orderGroupInput.type = 'hidden';
      orderGroupInput.name = 'properties[_Order Group]';
      form.appendChild(orderGroupInput);
    }

    // SPACEY: Debug: Check if input already has value (would indicate multi-capture)
    const existingValue = orderGroupInput.value;
    debugLog('Existing Order Group input value:', existingValue || 'none');

    // SPACEY: Always generate new Order Group ID - never reuse from previous submissions
    // Each add-to-cart should have unique Order Group ID for proper fee syncing
    const orderGroupId = generateOrderGroupId();
    orderGroupInput.value = orderGroupId;
    debugLog('Generated new order group ID:', orderGroupId);

    // SPACEY: Declare local variable for fee data (will be pushed to queue)
    const pendingFeeData = {
      orderGroupId,
      quantity,
      saLevy: hasSaLevy,
      setupFee: printTypeRadio ? {
        type: printTypeRadio.value,
        variantId: SETUP_FEES[printTypeRadio.value]
      } : null,
      lineFees: []
    };

    debugLog('Initial pending fee data:', pendingFeeData);

    // Helper: get label from element (supports both select and hidden input)
    function getLineFeeLabel(el) {
      if (el.tagName === 'SELECT') {
        const label = el.options[el.selectedIndex].text;
        debugLog('Getting label from SELECT:', { value: el.value, label });
        return label;
      }
      // Hidden input: derive label from fee value
      const labels = { '1-line': '1 Line', '2-lines': '2 Lines', '3-lines': '3 Lines', 'custom': 'Custom' };
      const label = labels[el.value] || el.value;
      debugLog('Getting label from hidden input:', { value: el.value, derivedLabel: label });
      return label;
    }

    // Single print lines (Allflex only — FOFIA line fees handled in the FOFIA block below)
    if (!isFofia && printLinesSelect && printLinesSelect.value && printLinesSelect.value !== 'blank') {
      debugLog('Processing single print lines...');
      const variantId = LINE_FEES[printLinesSelect.value];
      debugLog('Mapped print lines value to variant ID:', { 
        value: printLinesSelect.value, 
        variantId,
        exists: !!variantId 
      });
      if (variantId) {
        pendingFeeData.lineFees.push({
          variantId,
          label: getLineFeeLabel(printLinesSelect),
          side: null
        });
        debugLog('Added single print lines fee to pending data');
      } else {
        debugError('No variant ID found for print lines value:', printLinesSelect.value);
      }
    }

    // Female print lines (NLIS cattle) — Allflex only
    if (!isFofia && printLinesFemale && printLinesFemale.value && printLinesFemale.value !== 'blank') {
      debugLog('Processing female print lines...');
      const variantId = LINE_FEES[printLinesFemale.value];
      debugLog('Mapped female print lines value to variant ID:', { 
        value: printLinesFemale.value, 
        variantId,
        exists: !!variantId 
      });
      if (variantId) {
        pendingFeeData.lineFees.push({
          variantId,
          label: getLineFeeLabel(printLinesFemale),
          side: 'Female'
        });
        debugLog('Added female print lines fee to pending data');
      } else {
        debugError('No variant ID found for female print lines value:', printLinesFemale.value);
      }
    }

    // Male print lines (NLIS cattle) — Allflex only
    if (!isFofia && printLinesMale && printLinesMale.value && printLinesMale.value !== 'blank') {
      debugLog('Processing male print lines...');
      const variantId = LINE_FEES[printLinesMale.value];
      debugLog('Mapped male print lines value to variant ID:', { 
        value: printLinesMale.value, 
        variantId,
        exists: !!variantId 
      });
      if (variantId) {
        pendingFeeData.lineFees.push({
          variantId,
          label: getLineFeeLabel(printLinesMale),
          side: 'Male'
        });
        debugLog('Added male print lines fee to pending data');
      } else {
        debugError('No variant ID found for male print lines value:', printLinesMale.value);
      }
    }

    // Side printing fees (sheep/goat/feedlot) — Allflex only
    if (!isFofia && hasPrintSides) {
      debugLog('=== PROCESSING SIDE PRINTING FEES ===');
      debugLog('Print sides dropdown value:', printSidesSelect.value);
      debugLog('Available SIDE_FEES mappings:', SIDE_FEES);
      
      const variantId = SIDE_FEES[printSidesSelect.value];
      debugLog('Mapped sides value to variant ID:', { 
        value: printSidesSelect.value, 
        variantId,
        exists: !!variantId 
      });
      
      if (variantId) {
        pendingFeeData.lineFees.push({
          variantId,
          label: printSidesSelect.value,
          side: null
        });
        debugLog('Added side printing fee to pending data');
      } else {
        debugError('!!! CRITICAL: No variant ID found for sides value:', printSidesSelect.value);
        debugError('Available SIDE_FEES keys:', Object.keys(SIDE_FEES));
        debugError('Current sides dropdown value:', printSidesSelect.value);
      }
    }

    // ── FOFIA print fees (laser vs pitch black; feedlot flat; VID sides) — strictly vendor-gated ──
    if (isFofia) {
      debugLog('=== PROCESSING FOFIA FEES ===', {
        fofiaFeeMode,
        method: fofiaPrintMethod && fofiaPrintMethod.value,
        lines: printLinesSelect && printLinesSelect.value,
        sides: printSidesSelect && printSidesSelect.value
      });
      if (fofiaFeeMode === 'lines' && printLinesSelect && printLinesSelect.value && printLinesSelect.value !== 'blank') {
        const method = fofiaPrintMethod ? fofiaPrintMethod.value : 'Laser';
        const feeKey = method + '-' + printLinesSelect.value; // 'Laser-2-lines' / 'Pitch Black-1-line'
        const variantId = FOFIA_LINE_FEES[feeKey];
        if (variantId) {
          pendingFeeData.lineFees.push({ variantId, label: 'FOFIA ' + method + ' ' + getLineFeeLabel(printLinesSelect), side: null });
          debugLog('Added FOFIA line fee:', { feeKey, variantId });
        } else {
          debugError('FOFIA line fee key not found:', feeKey, 'available:', Object.keys(FOFIA_LINE_FEES));
        }
      } else if (fofiaFeeMode === 'feedlot' && hasPrintSides) {
        if (FOFIA_FEEDLOT_LASER_FEE) {
          pendingFeeData.lineFees.push({ variantId: FOFIA_FEEDLOT_LASER_FEE, label: 'FOFIA Feedlot Laser', side: null });
          debugLog('Added FOFIA feedlot laser fee');
        }
      } else if (fofiaFeeMode === 'vid' && hasPrintSides) {
        const sideMap = { 'Printed 1 Side': 'VID 1 Side', 'Printed 2 Sides': 'VID 2 Sides' };
        const variantId = FOFIA_SIDE_FEES[sideMap[printSidesSelect.value]];
        if (variantId) {
          pendingFeeData.lineFees.push({ variantId, label: 'FOFIA ' + printSidesSelect.value, side: null });
          debugLog('Added FOFIA VID side fee:', printSidesSelect.value);
        } else {
          debugError('FOFIA VID side fee not found for:', printSidesSelect.value);
        }
      }

      // Reverse/back-print add-on (lines-mode tags): if the box is ticked, add the
      // back-print fee that follows the front print method (Laser $0.30 / Pitch Black $0.95).
      if (fofiaFeeMode === 'lines' && fofiaBackPrint && fofiaBackPrint.checked) {
        const backMethod = fofiaPrintMethod ? fofiaPrintMethod.value : 'Laser';
        const backId = FOFIA_BACK_PRINT_FEES[backMethod];
        if (backId) {
          pendingFeeData.lineFees.push({ variantId: backId, label: 'FOFIA Back Print (' + backMethod + ')', side: null });
          debugLog('Added FOFIA back-print fee:', { backMethod, backId });
        } else {
          debugError('FOFIA back-print fee not found for method:', backMethod);
        }
      }
    }

    // SPACEY: Capture button pairing data (after all fee processing)
    if (hasButtonPairing) {
      debugLog('=== CAPTURING BUTTON PAIRING ===');
      const buttonVariantId = buttonPairingEl.dataset.buttonProductId;
      const buttonTitle = buttonPairingEl.dataset.buttonProductTitle;
      debugLog('Button variant ID:', buttonVariantId);
      debugLog('Button title:', buttonTitle);

      if (buttonVariantId) {
        pendingFeeData.buttonPairing = {
          variantId: buttonVariantId,
          title: buttonTitle
        };
        debugLog('Button pairing added to pending fee data');
      } else {
        debugError('!!! BUTTON VARIANT ID NOT FOUND !!!');
      }
    } else {
      debugLog('No button pairing selected or not available');
    }

    debugLog('=== CAPTURE COMPLETE ===');
    debugLog('Final fee data to queue:', pendingFeeData);
    debugLog('Number of fee items to be added:', pendingFeeData.lineFees.length + (pendingFeeData.setupFee ? 1 : 0) + (pendingFeeData.saLevy ? 1 : 0) + (pendingFeeData.buttonPairing ? 1 : 0));

    // Round-2 hardening (#4962): resolve the full /cart/add.js payload NOW —
    // variant ids, button colour and title all live on THIS page — and persist
    // it, so delivery can be replayed by addon-recovery.js on any later page
    // load if this page dies before the companion POST lands.
    pendingFeeData.items = buildFeeItems(pendingFeeData);
    persistAddonEntry({
      og: pendingFeeData.orderGroupId,
      items: pendingFeeData.items,
      created: Date.now(),
      lastAttempt: 0
    });

    // SPACEY: Push to queue instead of setting global variable (prevents race conditions)
    feeQueue.push(pendingFeeData);
    debugLog('Fee data queued. Queue length:', feeQueue.length);
    debugLog('=== CAPTURE COMPLETE, Queue Count:', window._captureCount, '==='); // SPACEY: Mark end of capture with count

    // SPACEY: DO NOT process queue here! Queue will be processed when Dawn
    // publishes cartUpdate event after successfully adding main product.
    // Processing in capture phase would interfere with Dawn's form submission.
  }

  /**
   * SPACEY: Process fee queue in FIFO order to prevent race conditions
   * Uses a loop instead of recursion to avoid mutex blocking the next item
   */
  async function processFeeQueue() {
    if (isProcessingQueue) {
      debugLog('Queue already processing, skipping (prevents race conditions)');
      return;
    }

    if (feeQueue.length === 0) {
      debugLog('Queue is empty, nothing to process');
      return;
    }

    debugLog('=== STARTING QUEUE PROCESS ===');
    debugLog('Queue length:', feeQueue.length);

    isProcessingQueue = true;

    try {
      // SPACEY: Process all items in queue using a loop (not recursion)
      // Recursion was blocked by mutex check, preventing subsequent items from processing
      while (feeQueue.length > 0) {
        const feeData = feeQueue.shift();
        debugLog('Processing fee data from queue:', feeData);
        debugLog('Remaining queue length:', feeQueue.length);

        await processFeeData(feeData);

        debugLog('Fee data processed successfully');
        debugLog('Continuing to next item in queue...');
      }
    } catch (error) {
      debugError('!!! ERROR PROCESSING FEE QUEUE !!!', error);
      debugError('Error stack:', error.stack);
    } finally {
      isProcessingQueue = false;
      debugLog('=== QUEUE PROCESS COMPLETE ===');
    }
  }

  /**
   * Handle cart update event from Dawn
   * SPACEY: Now triggers queue processing instead of direct processing
   */
  async function handleCartUpdate(event) {
    debugLog('=== CART UPDATE EVENT RECEIVED ===');
    debugLog('Event source:', event.source);
    debugLog('Event details:', event);

    // Only process if it came from product-form (Dawn's add to cart)
    if (event.source !== 'product-form') {
      debugLog('Event not from product-form, skipping');
      return;
    }

    // SPACEY: Check if we have items in queue
    if (feeQueue.length === 0) {
      debugLog('No fee data in queue, skipping');
      debugWarn('This might indicate captureFormData was not called or returned early');
      return;
    }

    debugLog('Fee queue has items, triggering processing...');

    // SPACEY: Queue processing happens automatically, but we trigger it here on cart update
    try {
      await processFeeQueue();
    } catch (error) {
      debugError('!!! FEE QUEUE PROCESSING FAILED !!!', error);
      debugError('Error stack:', error.stack);
    }
  }

  /**
   * HARDENING (2026-07-17, order #4512 — paid add-on silently lost): the add-on
   * POST is the only thing standing between "Matching Buttons: Yes" recorded on
   * the tag and the paid button/fee products actually reaching the cart. This
   * helper makes that delivery leg survivable:
   *   - keepalive: the POST completes even if the customer navigates to the
   *     cart/checkout (or closes the page) while it is in flight.
   *   - verified single retry: on failure it first re-reads /cart.js and checks
   *     for this order group's add-on lines, so a lost RESPONSE can never cause
   *     a double-add; only a confirmed-missing add is retried, once.
   * Vendor-agnostic by design — runs after the page's Liquid layer has already
   * decided WHICH button/fee variants apply (Allflex vs FOFIA metafields).
   * Returns the /cart/add.js response data, or null if delivery finally failed.
   */
  async function postAddonsVerified(feeItems, orderGroupId) {
    async function post(items) {
      const res = await fetchWithTimeout('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: items }),
        keepalive: true
      });
      if (!res.ok) {
        const errorText = await res.text().catch(() => '');
        debugError('!!! ADD-ON POST FAILED !!!', res.status, errorText);
        return null;
      }
      return res.json().catch(() => ({}));
    }
    // Per-item cart diff: which of this group's items are NOT in the cart yet?
    // (#4962 lesson: the old check passed if ANY companion line existed for the
    // group, so one delivered fee line could suppress the retry for a lost
    // button. Now every item must be individually present.)
    async function missingFromCart() {
      const cart = await fetchWithTimeout('/cart.js', {}, 8000).then((r) => r.json());
      return feeItems.filter((item) => !cart.items.some((line) =>
        String(line.variant_id) === String(item.id) &&
        (line.properties || {})['_Order Group'] === orderGroupId
      ));
    }

    let data = null;
    try { data = await post(feeItems); } catch (e) { debugError('Add-on POST threw:', e); }
    if (data) return data;

    await new Promise((r) => setTimeout(r, 1500));
    let missing;
    try { missing = await missingFromCart(); } catch (e) {
      debugError('Cart verification failed:', e);
      missing = feeItems; // can't verify — assume everything is still owed
    }
    if (missing.length === 0) {
      debugWarn('Add-on POST reported failure but all items ARE in the cart — no retry needed.');
      return {};
    }
    // Retry the missing items INDIVIDUALLY: /cart/add.js with an items array is
    // all-or-nothing, so one unaddable variant must not take down the rest.
    debugWarn('Add-ons missing from cart — retrying individually for', orderGroupId, missing.map((m) => m.id));
    let delivered = 0;
    for (const item of missing) {
      try { if (await post([item])) delivered++; } catch (e) { debugError('Add-on retry threw:', e); }
    }
    if (delivered < missing.length) {
      debugError('Add-on delivery incomplete:', delivered, 'of', missing.length, '— persisted entry left for addon-recovery.js');
      return null;
    }
    // Everything re-delivered — verify once more before declaring success.
    try { missing = await missingFromCart(); } catch (e) { missing = []; }
    return missing.length === 0 ? {} : null;
  }

  /**
   * SPACEY: Process a single fee data item (extracted from handleCartUpdate)
   */
  /**
   * Build the full /cart/add.js payload for one captured order group.
   * Called at CAPTURE time (on the PDP, at submit) so every DOM read — button
   * colour, product title, variant ids — reflects the configuration the
   * customer actually submitted. Previously the button colour/title were read
   * at PROCESSING time, so when two groups drained in one loop both buttons
   * got whatever colour was currently selected.
   */
  function buildFeeItems(feeData) {
    // Build fee items array
    const feeItems = [];

      // SA Levy
      if (feeData.saLevy) {
        debugLog('Adding SA Levy fee...');
        feeItems.push({
          id: SA_LEVY_VARIANT_ID,
          quantity: feeData.quantity,
          properties: {
            '_Auto-added fee': 'true',
            '_Fee type': 'levy',
            '_SA Levy': 'Biosecurity Levy',
            '_Order Group': feeData.orderGroupId
          }
        });
        debugLog('SA Levy fee item:', { id: SA_LEVY_VARIANT_ID, quantity: feeData.quantity });
      } else {
        debugLog('No SA Levy fee to add');
      }

      // Setup fee (one-time)
      if (feeData.setupFee?.variantId) {
        debugLog('Adding setup fee...');
        feeItems.push({
          id: feeData.setupFee.variantId,
          quantity: 1,
          properties: {
            '_Auto-added fee': 'true',
            '_Fee type': 'setup',
            '_Print Setup': feeData.setupFee.type,
            '_Order Group': feeData.orderGroupId
          }
        });
        debugLog('Setup fee item:', { 
          id: feeData.setupFee.variantId, 
          type: feeData.setupFee.type 
        });
      } else {
        debugLog('No setup fee to add');
      }

      // Line fees (per-tag)
      debugLog('Adding line fees...');
      for (const lineFee of feeData.lineFees) {
        debugLog('Processing line fee:', lineFee);
        feeItems.push({
          id: lineFee.variantId,
          quantity: feeData.quantity,
          properties: {
            '_Auto-added fee': 'true',
            '_Fee type': 'line',
            '_Print Lines': lineFee.label,
            '_Order Group': feeData.orderGroupId,
            ...(lineFee.side && { '_Tag Side': lineFee.side })
          }
        });
      }
      debugLog('Total line fees to add:', feeData.lineFees.length);

      // SPACEY: Add button pairing if selected
      if (feeData.buttonPairing) {
        debugLog('=== ADDING BUTTON PAIRING ===');
        debugLog('Button pairing data:', feeData.buttonPairing);

        // Get button properties from form
        const tagTitle = document.querySelector('.product__title h1, .product__title h2')
          ? document.querySelector('.product__title h1, .product__title h2').textContent.trim()
          : 'Tag product';

        const selectedColor = getSelectedColorForButton();

        const buttonProps = {
          '_Button pair': 'true',
          '_Order Group': feeData.orderGroupId,
          'Paired with': tagTitle
        };
        if (selectedColor) {
          buttonProps['Colour'] = selectedColor;
        }

        feeItems.push({
          id: feeData.buttonPairing.variantId,
          quantity: feeData.quantity,
          properties: buttonProps
        });
        debugLog('Button pairing item added to feeItems:', {
          id: feeData.buttonPairing.variantId,
          quantity: feeData.quantity
        });
      } else {
        debugLog('No button pairing to add');
      }

    return feeItems;
  }

  /**
   * SPACEY: Process a single fee data item (extracted from handleCartUpdate)
   */
  async function processFeeData(feeData) {
    debugLog('=== PROCESSING FEE ADDITION ===');
    debugLog('Fee data to process:', feeData);

    try {
      // Payload is normally built (and persisted) at capture time; fall back to
      // building now for any entry that predates capture-time payloads.
      const feeItems = (feeData.items && feeData.items.length) ? feeData.items : buildFeeItems(feeData);

      if (feeItems.length === 0) {
        debugLog('No fee items or buttons to add, skipping API call');
        unpersistAddonEntry(feeData.orderGroupId);
        return;
      }

      debugLog('=== SENDING AJAX REQUEST TO ADD FEES ===');
      debugLog('Total fee items to add:', feeItems.length);
      debugLog('Request payload:', { items: feeItems });

      // Add all fees in one API call — keepalive + per-item verified repair
      // (postAddonsVerified; order #4512 + #4962 hardening).
      stampAddonAttempt(feeData.orderGroupId);
      const responseData = await postAddonsVerified(feeItems, feeData.orderGroupId);
      if (!responseData) {
        throw new Error('Failed to add fees after verified retry');
      }
      // Delivery verified in the cart — the persisted entry has done its job.
      unpersistAddonEntry(feeData.orderGroupId);
      debugLog('Success! Response data:', responseData);
      debugLog('Total fees added:', feeItems.length);

      // SPACEY: Refresh all cart sections after adding fees
      // We can't rely on Dawn's onCartUpdate() - it's incomplete
      debugLog('Refreshing all cart sections after fee addition');
      await refreshAllCartSections();

      debugLog('=== FEE ADDITION COMPLETE ===');

      // SPACEY: Publish completion event so other scripts (button-pairing.js) know fees are done
      // This eliminates timing hacks and enables deterministic coordination
      if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'fee-auto-add-complete',
          cartData: responseData
        });
        debugLog('✓ Published fee-auto-add-complete event');
      }

    } catch (error) {
      debugError('!!! FEE ADDITION FAILED !!!');
      debugError('Error details:', error);
      debugError('Error stack:', error.stack);
    }
  }

  /**
   * SPACEY: Refresh all cart sections using shared helper (Dawn's Section Rendering API pattern)
   * Dawn's onCartUpdate() is incomplete - this implements the full update
   */
  async function refreshAllCartSections() {
    // SPACEY: Use shared helper for consistent UI refresh
    if (window.CartSharedHelpers?.refreshAllCartSections) {
      await window.CartSharedHelpers.refreshAllCartSections(debug);
    } else {
      debugError('CartSharedHelpers not available - cannot refresh cart sections');
    }
  }

  /**
   * Initialize the fee auto-add system
   */
  function init() {
    debugLog('=== INITIALIZING FEE SYSTEM ===');
    debugLog('Checking for Dawn pub/sub system...');
    
    // Capture form data before Dawn processes it (capture phase runs first)
    document.addEventListener('submit', captureFormData, true);
    debugLog('Added submit event listener (capture phase)');

    // Repair fee-mirror desync after back-nav/bfcache form restoration too, so
    // the PDP price estimate stays honest before the customer even submits.
    window.addEventListener('pageshow', syncPrintFeeMirrors);

    // Last-chance flush (2026-07-17, order #4512): if the page is being left
    // while add-ons are still queued (Dawn's cartUpdate never fired — e.g. the
    // parent product went in via full form navigation, or the customer hit
    // checkout before the event), start processing NOW. Item building is
    // synchronous and the POST uses keepalive, so the request is dispatched
    // before unload and completes after it. Without this the queue dies with
    // the page and the paid add-on is silently lost.
    window.addEventListener('pagehide', () => {
      if (feeQueue.length === 0) return;
      debugWarn('pagehide with', feeQueue.length, 'queued add-on group(s) — flushing with keepalive');
      // Dispatch EVERY group's POST synchronously. The old path called
      // processFeeQueue(), whose awaited loop only ever dispatched the FIRST
      // group before the page died — group 2+ was silently dropped. The
      // persisted entries stay in localStorage; addon-recovery.js verifies and
      // cleans them up on the next page load, so a keepalive POST that lands
      // is never double-added and one that dies is replayed.
      while (feeQueue.length > 0) {
        const feeData = feeQueue.shift();
        const items = (feeData.items && feeData.items.length) ? feeData.items : buildFeeItems(feeData);
        if (!items.length) { unpersistAddonEntry(feeData.orderGroupId); continue; }
        stampAddonAttempt(feeData.orderGroupId);
        try {
          fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items: items }),
            keepalive: true
          });
        } catch (e) { /* recovery layer will replay */ }
      }
    });

    // Subscribe to Dawn's cart update event
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      debugLog('Dawn pub/sub available');
      debugLog('Subscribing to cartUpdate event');
      subscribe(PUB_SUB_EVENTS.cartUpdate, handleCartUpdate);
      debugLog('✓ Fee system initialized with pub/sub integration');
    } else {
      debugWarn('!!! Dawn pub/sub NOT available !!!');
      debugWarn('Falling back to polling mode');
      // Fallback: check for cart changes periodically (not ideal but works)
      initFallbackMode();
    }
    
    debugLog('=== FEE SYSTEM INITIALIZATION COMPLETE ===');
  }

  /**
   * Fallback mode if pub/sub isn't available
   */
  function initFallbackMode() {
    debugLog('=== INITIALIZING FALLBACK POLLING MODE ===');
    debugLog('Polling interval: 500ms');
    
    let lastCartToken = null;

    setInterval(async () => {
      // SPACEY: Check queue instead of old pendingFeeData variable
      if (feeQueue.length === 0) return;

      try {
        const cart = await fetch('/cart.js').then(r => r.json());
        if (cart.token !== lastCartToken) {
          debugLog('Fallback mode: Detected cart change');
          lastCartToken = cart.token;
          // Cart changed, process fees
          await handleCartUpdate({ source: 'product-form' });
        }
      } catch (e) {
        debugError('Fallback mode polling error:', e);
      }
    }, 500);
  }

  // Export for external use
  window.feeAutoAdd = {
    ALL_FEE_VARIANT_IDS
  };

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
